import apiClient from "./apiClient";
import axios from "axios";
import { useAtom } from "jotai";
import { fileEntitiesAtom } from "../atoms/fileAtoms";
import { FIVE_MINUTES_MS } from "../utils/consts";
import { useShowService } from "./showService";
import { useRunService } from "./runService";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useRecordedService } from "./recordedService";
import { useReleaseService } from "./releaseService";
import {
  FileEntity,
  FileEntityWithContents,
} from "@quantizr-engineering/common/types/file";

export const useFileService = () => {
  const [fileEntities, setFileEntities] = useAtom(fileEntitiesAtom);
  const { getShows } = useShowService();
  const { getRuns } = useRunService();
  const { getReleases } = useReleaseService();
  const { getRecorded: getRecordings } = useRecordedService();

  useEffect(() => {
    cacheGetFileEntities();
  }, []);

  const getFileEntities = async () => {
    setFileEntities((prev) => ({ ...prev, loading: true }));
    try {
      const response = await apiClient.get("/file");
      setFileEntities((prev) => ({
        loading: false,
        data: response.data,
        lastFetched: Date.now(),
      }));
      return response;
    } catch (error) {
      setFileEntities((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      throw error;
    }
  };

  const cacheGetFileEntities = async () => {
    if (!!fileEntities.loading) {
      return;
    }
    if (
      !fileEntities.lastFetched ||
      Date.now() - fileEntities.lastFetched > FIVE_MINUTES_MS
    ) {
      await getFileEntities();
    }
  };

  const patchDoc = async (doc: FileEntity) => {
    try {
      const response = await apiClient.patch(`/file/${doc.id}`, doc, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      toast.success("Document updated successfully!");
      getFileEntities();
      return response;
    } catch (error) {
      toast.error("Failed to update document.");
      console.error("error");
      throw error;
    }
  };

  const fetchPdfContent = useCallback(
    async (documentId: number): Promise<ArrayBuffer> => {
      try {
        const authToken = localStorage.getItem("authToken");

        const response = await axios.get(
          `${import.meta.env.VITE_QZ_MONOLITH_URL}/file/${documentId}/content`,
          {
            responseType: "arraybuffer",
            headers: {
              Accept: "application/pdf",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const handleFileUpload = async (
    files: File[],
    endpoint: string,
    onSuccess: () => void,
    singleFile: boolean = false
  ): Promise<FileEntity[]> => {
    const formData = new FormData();
    let responseData: FileEntity[] = [];
    if (singleFile) {
      formData.append("file", files[0]);
    } else {
      files.forEach((file) => {
        formData.append(`files`, file);
      });
    }
    const authToken = localStorage.getItem("authToken");

    try {
      const response = await apiClient.post(
        `${import.meta.env.VITE_QZ_MONOLITH_URL}${endpoint}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        if (response.headers["qz-message"]) {
          toast.success(response.headers["qz-message"]);
        } else {
          throw new Error(response.statusText || "Unknown error.");
        }
        responseData = response.data as FileEntity[];
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.status === 500 &&
          error.response?.data?.message?.includes("duplicate")
            ? "Duplicate file detected. Please upload a different file."
            : error.response?.data?.message || "Upload failed.";

        toast.error(errorMessage);
        throw new Error(errorMessage);
      } else if (error instanceof Error) {
        toast.error(`Upload failed: ${error.message}`);
        throw new Error(`Upload failed: ${error.message}`);
      } else {
        toast.error("Upload failed: An unexpected error occurred.");
        throw new Error("Upload failed: An unexpected error occurred.");
      }
    } finally {
      onSuccess();
      getShows();
      getRuns();
      return responseData;
    }
  };

  const handleArbitraryFileUpload = async (
    files: FileEntityWithContents[],
    fileEntities: Partial<FileEntity>[],
    endpoint: string,
    onSuccess: () => void,
    singleFile: boolean = false
  ): Promise<FileEntity[]> => {
    const formData = new FormData();
    let responseData: FileEntity[] = [];
    if (singleFile) {
      formData.append("file", files[0]);
    } else {
      files.forEach((file) => {
        formData.append(`files`, file);
      });
    }
    formData.append("fileEntities", JSON.stringify(fileEntities));
    const authToken = localStorage.getItem("authToken");

    try {
      const response = await apiClient.post(
        `${import.meta.env.VITE_QZ_MONOLITH_URL}${endpoint}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        if (response.headers["qz-message"]) {
          toast.success(response.headers["qz-message"]);
        } else {
          throw new Error(response.statusText || "Unknown error.");
        }
        responseData = response.data as FileEntity[];
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.status === 500 &&
          error.response?.data?.message?.includes("duplicate")
            ? "Duplicate file detected. Please upload a different file."
            : error.response?.data?.message || "Upload failed.";

        toast.error(errorMessage);
        throw new Error(errorMessage);
      } else if (error instanceof Error) {
        toast.error(`Upload failed: ${error.message}`);
        throw new Error(`Upload failed: ${error.message}`);
      } else {
        toast.error("Upload failed: An unexpected error occurred.");
        throw new Error("Upload failed: An unexpected error occurred.");
      }
    } finally {
      onSuccess();
      getShows();
      getRuns();
      return responseData;
    }
  };

  const uploadRecorded = async (files: File[]): Promise<void> => {
    await handleFileUpload(files, "/file/recorded", () => {
      getReleases();
      getRecordings();
    });
  };

  const uploadArbitrary = async (
    files: File[],
    fileEntities: Partial<FileEntity>[]
  ): Promise<FileEntity[]> => {
    const res = await handleArbitraryFileUpload(
      files,
      fileEntities,
      "/file/arbitrary",
      () => {
        getShows();
        getRuns();
      }
    );
    return res;
  };



  const pollFileProcessingStatus = async (id: string) => {
    try {
      const statusResponse = await apiClient.get(`/file/status/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const response = {
        id: statusResponse.data.id,
        filename: statusResponse.data.filename,
        status: statusResponse.data.status,
      };

      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteFile = async (id: string) => {
    try {
      const response = await apiClient.delete(`/file/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setFileEntities((prev) => ({
        ...prev,
        loading: false,
        data: prev?.data?.filter((file) => file.id !== id) ?? [],
        lastFetched: null,
      }));
      toast.success("File deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete file.");
      console.error("error");
      throw error;
    }
  };

  return {
    fileEntities: fileEntities.data,
    fileEntitiesLoading: fileEntities.loading,
    getFileEntities,
    cacheGetFileEntities,
    patchDoc,
    fetchPdfContent,
    uploadRecorded,
    uploadArbitrary,
    pollFileProcessingStatus,
    deleteFile,
  };
};
